import type { CSSProperties, ReactNode } from 'react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useWorkspaceSidebar } from './WorkspaceSidebarContext';
import {
  WORKSPACE_HEADER_HEIGHT,
  WORKSPACE_SIDEBAR_BORDER_CLASS,
  WORKSPACE_SIDEBAR_BG_CLASS,
  WORKSPACE_SIDEBAR_EXPANDED_WIDTH,
  WORKSPACE_SIDEBAR_WIDTH,
} from './workspace-layout';
import TopNavBar from './TopNavBar';

type WorkspaceShellProps = {
  sidebar?: ReactNode;
  children: ReactNode;
  mainClassName?: string;
  noSidebar?: boolean;
  /** Desktop: widen the rail on hover as an overlay so labels can show. */
  expandSidebarOnHover?: boolean;
};

/**
 * True application shell — one parent frame for sidebar + header + content.
 *
 * ┌──────────┬────────────────────┐
 * │ Sidebar  │ Header             │  ← shared cream band, no vertical border
 * │          ├────────────────────┤  ← single subtle bottom border under header only
 * │          │ Main               │
 * └──────────┴────────────────────┘
 *
 * Header is in document flow (not a separate fixed MainLayout chrome),
 * so it shares layout with the sidebar and sits flush to the viewport top.
 */
const WorkspaceShell = ({
  sidebar,
  children,
  mainClassName,
  noSidebar = false,
  expandSidebarOnHover = false,
}: WorkspaceShellProps) => {
  const { pathname } = useLocation();
  const sidebarCtx = useWorkspaceSidebar();
  const mobileOpen = sidebarCtx?.mobileOpen ?? false;
  const closeMobile = sidebarCtx?.closeMobile;

  useEffect(() => {
    closeMobile?.();
  }, [pathname, closeMobile]);

  return (
    <div
      className="flex h-screen w-full overflow-hidden bg-[var(--inv-bg)]"
      style={
        {
          '--workspace-sidebar-width': noSidebar ? '0px' : WORKSPACE_SIDEBAR_WIDTH,
          '--workspace-sidebar-expanded-width': WORKSPACE_SIDEBAR_EXPANDED_WIDTH,
          '--workspace-header-height': WORKSPACE_HEADER_HEIGHT,
        } as CSSProperties
      }>
      {mobileOpen && !noSidebar ? (
        <Button
          type="button"
          variant="ghost"
          className="fixed inset-0 z-[115] h-auto cursor-default rounded-none border-0 bg-transparent p-0 hover:bg-transparent min-[901px]:hidden"
          aria-label="Close navigation menu"
          onClick={() => closeMobile?.()}
        />
      ) : null}

      {!noSidebar && (
        <aside
          className={cn(
            'box-border h-full min-h-0 w-[var(--workspace-sidebar-width)] shrink-0 overflow-hidden',
            'border-0 border-r border-solid',
            WORKSPACE_SIDEBAR_BORDER_CLASS,
            WORKSPACE_SIDEBAR_BG_CLASS,
            // Above TopNavBar (z-110) so mobile drawer shows full brand header
            'max-[900px]:fixed max-[900px]:top-0 max-[900px]:left-0 max-[900px]:z-[120] max-[900px]:h-screen',
            'max-[900px]:w-16',
            'max-[900px]:transition-transform max-[900px]:duration-300 max-[900px]:ease-out',
            'max-[900px]:shadow-[4px_0_24px_rgba(45,42,38,0.1)]',
            mobileOpen
              ? 'max-[900px]:translate-x-0'
              : 'max-[900px]:-translate-x-full',
            'min-[901px]:relative min-[901px]:translate-x-0 min-[901px]:shadow-none',
            expandSidebarOnHover && 'min-[901px]:z-[115] min-[901px]:overflow-visible',
          )}>
          {expandSidebarOnHover ? (
            <div
              className={cn(
                'group/sidebar box-border h-full w-full',
                WORKSPACE_SIDEBAR_BG_CLASS,
                'min-[901px]:absolute min-[901px]:inset-y-0 min-[901px]:left-0 min-[901px]:w-[var(--workspace-sidebar-width)] min-[901px]:overflow-hidden',
                'min-[901px]:border-0 min-[901px]:border-r min-[901px]:border-solid',
                WORKSPACE_SIDEBAR_BORDER_CLASS,
                'min-[901px]:transition-[width,box-shadow] min-[901px]:duration-200 min-[901px]:ease-out',
                'min-[901px]:hover:w-[var(--workspace-sidebar-expanded-width)] min-[901px]:hover:delay-100',
                'min-[901px]:hover:shadow-[4px_0_24px_rgba(45,42,38,0.12)]',
                'min-[901px]:has-[:focus-visible]:w-[var(--workspace-sidebar-expanded-width)]',
              )}>
              {sidebar}
            </div>
          ) : (
            sidebar
          )}
        </aside>
      )}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-[var(--inv-bg)]">
        <TopNavBar
          navLinks={[]}
          variant="inventory"
          showMobileMenu={!noSidebar}
          embedded
        />

        <main
          className={cn(
            'box-border min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto bg-[var(--inv-bg)]',
            mobileOpen &&
              !noSidebar &&
              'max-[900px]:pointer-events-none max-[900px]:select-none',
            mainClassName,
          )}
          aria-hidden={mobileOpen && !noSidebar ? true : undefined}>
          {children}
        </main>
      </div>
    </div>
  );
};

export default WorkspaceShell;
